/**
 * KPI Configuration
 * 
 * Configuração centralizada dos KPIs do dashboard.
 * Facilita adicionar, remover ou modificar KPIs.
 * 
 * @module utils/dashboard/kpiConfig
 */

import {
    FaUsers,
    FaStethoscope,
    FaCheckCircle,
    FaClock,
    FaWarehouse,
    FaTablets,
    FaUserCheck,
    FaExclamationTriangle,
} from 'react-icons/fa';

/**
 * Configuração dos KPIs do dashboard
 * 
 * Cada KPI possui:
 * - key: identificador único
 * - label: rótulo exibido
 * - unit: unidade de medida (%, R$, etc.)
 * - icon: componente de ícone
 * - color: cor do ícone (classe Tailwind)
 * - bg: cor de fundo do ícone (classe Tailwind)
 * - dataKey: chave no objeto summary para buscar o valor
 * - category: categoria do KPI ('main' ou 'alert')
 * 
 * @type {Array<Object>}
 */
const kpiConfig = [
    // KPIs Principais
    {
        key: 'pacientes_ativos',
        label: 'Pacientes Ativos',
        unit: '',
        icon: FaStethoscope,
        color: 'text-green-600',
        bg: 'bg-green-100',
        dataKey: 'pacientesAtivos',
        category: 'main',
    },
    {
        key: 'profissionais_ativos',
        label: 'Profissionais Ativos',
        unit: '',
        icon: FaUsers,
        color: 'text-blue-600',
        bg: 'bg-blue-100',
        dataKey: 'usuariosAtivos',
        category: 'main',
    },
    {
        key: 'taxa_conformidade',
        label: 'Taxa de Conformidade',
        unit: '%',
        icon: FaCheckCircle,
        color: 'text-indigo-600',
        bg: 'bg-indigo-100',
        dataKey: 'taxaConformidade',
        category: 'main',
    },
    {
        key: 'eficiencia_ponto',
        label: 'Eficiência do Ponto',
        unit: '%',
        icon: FaClock,
        color: 'text-purple-600',
        bg: 'bg-purple-100',
        dataKey: 'eficienciaPonto',
        category: 'main',
    },
    {
        key: 'ocupacao_equipamentos',
        label: 'Ocupação de Equipamentos',
        unit: '%',
        icon: FaWarehouse,
        color: 'text-orange-600',
        bg: 'bg-orange-100',
        dataKey: 'taxaOcupacaoEquipamentos',
        category: 'main',
    },
    {
        key: 'utilizacao_medicamentos',
        label: 'Utilização de Medicamentos',
        unit: '%',
        icon: FaTablets,
        color: 'text-teal-600',
        bg: 'bg-teal-100',
        dataKey: 'taxaUtilizacaoMedicamentos',
        category: 'main',
    },
    {
        key: 'adesao_profissionais',
        label: 'Adesão de Profissionais',
        unit: '%',
        icon: FaUserCheck,
        color: 'text-cyan-600',
        bg: 'bg-cyan-100',
        dataKey: 'taxaAdesaoProfissionais',
        category: 'main',
    },
    // KPIs de Alertas
    {
        key: 'alertas_estoque',
        label: 'Alertas de Estoque/Validade',
        unit: '',
        icon: FaExclamationTriangle,
        color: 'text-red-600',
        bg: 'bg-red-100',
        dataKey: 'alertasEstoque',
        category: 'alert',
    },
    {
        key: 'tarefas_pendentes_hoje',
        label: 'Tarefas Pendentes HOJE',
        unit: '',
        icon: FaExclamationTriangle,
        color: 'text-yellow-600',
        bg: 'bg-yellow-100',
        dataKey: 'tarefasPendentesHoje',
        category: 'alert',
    },
    {
        key: 'tarefas_atrasadas',
        label: 'Tarefas Atrasadas',
        unit: '',
        icon: FaClock,
        color: 'text-red-600',
        bg: 'bg-red-100',
        dataKey: 'tarefasAtrasadasTotal',
        category: 'alert',
    },
];

/**
 * Filtra KPIs por categoria
 * @param {string} category - Categoria ('main' ou 'alert')
 * @returns {Array} Array de KPIs da categoria especificada
 */
export const getKPIsByCategory = (category) => {
    return kpiConfig.filter(kpi => kpi.category === category);
};

/**
 * Busca um KPI por sua chave
 * @param {string} key - Chave do KPI
 * @returns {Object|null} Configuração do KPI ou null se não encontrado
 */
export const getKPIByKey = (key) => {
    return kpiConfig.find(kpi => kpi.key === key) || null;
};

/**
 * Gera props de KPI a partir da configuração e dados do summary
 * @param {Object} kpiConfig - Configuração do KPI
 * @param {Object} summary - Dados do summary do dashboard
 * @returns {Object} Props prontas para o KPICard
 */
export const generateKPIProps = (kpiConfig, summary) => {
    return {
        label: kpiConfig.label,
        value: summary?.[kpiConfig.dataKey],
        unit: kpiConfig.unit,
        icon: kpiConfig.icon,
        color: kpiConfig.color,
        bg: kpiConfig.bg,
    };
};

export default kpiConfig;
